// ============================================================
// reconcile409.ts
// Shared helpers for COLUMN-AWARE 409 recovery (approach c, client-side).
//
// The backend guard is column-BLIND: it 409s whenever the row's updatedAt moved
// since the client's base, even if the other save touched DIFFERENT columns. To
// avoid that spurious conflict, on a 409 the save hook re-fetches the record and
// asks: did the server actually change any of the columns THIS save touched
// (vs the client's base values)? If not, it's safe to re-apply silently; if so,
// it's a genuine same-column conflict and surfaces as before.
//
// Comparison is done on the hook's own FLATTENED representation (the same shape
// it diffs against), so null/'' and arrays/booleans compare consistently.
// ============================================================

// Value equality for a flattened column. null/undefined are treated as '' (the
// flatten already coerces empties that way), objects/arrays compared by JSON.
export const flatEqual = (a: unknown, b: unknown): boolean => {
  const na = a === undefined || a === null ? '' : a
  const nb = b === undefined || b === null ? '' : b
  if (typeof na === 'object' || typeof nb === 'object') {
    try { return JSON.stringify(na) === JSON.stringify(nb) } catch { return na === nb }
  }
  return na === nb
}

// TRUE if the server changed any of the columns THIS save touched, versus the
// client's base values → a genuine same-column conflict. FALSE → the other save
// touched only other columns (non-overlap) → safe to re-apply this save.
// `mineFlat` (optional): the values THIS save is sending. A column the server
// already holds with exactly OUR value isn't a conflict — typically our own
// earlier, overlapping save landed first (it advanced updatedAt, so this one
// 409'd). Without this, the user's own save was reported as "updated by
// someone else" and their newer edit dropped.
export const serverChangedTouchedColumns = (
  touchedCols: string[],
  baseFlat: Record<string, unknown>,
  serverFlat: Record<string, unknown>,
  mineFlat?: Record<string, unknown>,
): boolean => touchedCols.some((col) =>
  !flatEqual(serverFlat[col], baseFlat[col])
  && !(mineFlat && flatEqual(serverFlat[col], mineFlat[col])))

// Cap on silent re-apply attempts: each re-apply can itself 409 if yet another
// concurrent save lands. After the cap we stop looping and surface the conflict.
// ★ (Audit M2) The case's DATE-only columns (Prisma @db.Date) come back from
// the server as ISO timestamps ("2024-02-04T00:00:00.000Z") while the page
// holds "2024-02-04" — compared raw they ALWAYS differ, so a harmless 409
// looked like a real clash and the person's edit was dropped. Normalise them
// to the page's format before comparing. (The case's other dates are plain
// text columns and come back exactly as saved.)
export const CASE_DATE_ONLY_COLUMNS = ['dateOfKnowledge', 'dateOfDeath', 'limitationOverrideDate'] as const
export const normaliseServerDates = (server: Record<string, unknown>): Record<string, unknown> => {
  const out = { ...server }
  for (const k of CASE_DATE_ONLY_COLUMNS) {
    const v = out[k]
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) out[k] = v.slice(0, 10)
  }
  return out
}

export const MAX_409_REAPPLY_ATTEMPTS = 3
